import React, { useEffect, useState } from "react";
import followUpReportService, {
  FilterCondition,
  FollowUpBooking,
  FollowUpFilters,
  KPIs,
  Project,
} from "../services/followUpReportService";
import "../css/FollowUpReport.css";

const FollowUpReport: React.FC = () => {
  // Filters
  const [selectedProject, setSelectedProject] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");

  // Dynamic filter state
  const [filterLogic, setFilterLogic] = useState<"AND" | "OR">("AND");
  const [filterConditions, setFilterConditions] = useState<FilterCondition[]>([]);

  // Data state
  const [projects, setProjects] = useState<Project[]>([]);
  const [kpis, setKpis] = useState<KPIs>({
    totalBookings: 0,
    totalPending: 0,
    totalReceived: 0,
    averagePendingPercent: 0,
    highRiskBookings: 0,
  });
  const [bookings, setBookings] = useState<FollowUpBooking[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFilterBuilder, setShowFilterBuilder] = useState(false);

  // Available filter fields and operators
  const filterFields = [
    { value: "pendingPercent", label: "Pending %" },
    { value: "receivedPercent", label: "Received %" },
    { value: "pendingAmount", label: "Pending Amount" },
    { value: "daysSinceBooking", label: "Days Since Booking" },
    { value: "daysSinceLastPayment", label: "Days Since Last Payment" },
    { value: "paymentCount", label: "Payment Count" },
    { value: "totalAmount", label: "Total Amount" },
    { value: "receivedAmount", label: "Received Amount" },
    { value: "paymentTerms", label: "Payment Terms" },
    { value: "unitType", label: "Unit Type" },
    { value: "wing", label: "Wing" },
    { value: "status", label: "Status" },
  ];

  const operatorsByField: Record<string, { value: string; label: string }[]> = {
    pendingPercent: [
      { value: ">", label: ">" },
      { value: ">=", label: ">=" },
      { value: "<", label: "<" },
      { value: "<=", label: "<=" },
      { value: "=", label: "=" },
      { value: "between", label: "Between" },
    ],
    receivedPercent: [
      { value: ">", label: ">" },
      { value: ">=", label: ">=" },
      { value: "<", label: "<" },
      { value: "<=", label: "<=" },
      { value: "=", label: "=" },
      { value: "between", label: "Between" },
    ],
    pendingAmount: [
      { value: ">", label: ">" },
      { value: ">=", label: ">=" },
      { value: "<", label: "<" },
      { value: "<=", label: "<=" },
      { value: "=", label: "=" },
      { value: "between", label: "Between" },
    ],
    daysSinceBooking: [
      { value: ">", label: ">" },
      { value: ">=", label: ">=" },
      { value: "<", label: "<" },
      { value: "<=", label: "<=" },
      { value: "between", label: "Between" },
    ],
    daysSinceLastPayment: [
      { value: ">", label: ">" },
      { value: ">=", label: ">=" },
      { value: "<", label: "<" },
      { value: "<=", label: "<=" },
      { value: "between", label: "Between" },
    ],
    paymentCount: [
      { value: "=", label: "=" },
      { value: "!=", label: "!=" },
      { value: ">", label: ">" },
      { value: ">=", label: ">=" },
      { value: "<", label: "<" },
      { value: "<=", label: "<=" },
    ],
    totalAmount: [
      { value: ">", label: ">" },
      { value: ">=", label: ">=" },
      { value: "<", label: "<" },
      { value: "<=", label: "<=" },
      { value: "between", label: "Between" },
    ],
    receivedAmount: [
      { value: ">", label: ">" },
      { value: ">=", label: ">=" },
      { value: "<", label: "<" },
      { value: "<=", label: "<=" },
      { value: "between", label: "Between" },
    ],
    paymentTerms: [
      { value: "=", label: "=" },
      { value: "!=", label: "!=" },
    ],
    unitType: [
      { value: "=", label: "=" },
      { value: "!=", label: "!=" },
    ],
    wing: [
      { value: "=", label: "=" },
      { value: "!=", label: "!=" },
    ],
    status: [
      { value: "=", label: "=" },
      { value: "!=", label: "!=" },
    ],
  };

  // Load data on mount and filter changes
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build filters payload
      const filters: FollowUpFilters | undefined =
        filterConditions.length > 0
          ? {
              logic: filterLogic,
              conditions: filterConditions,
            }
          : undefined;

      const data = await followUpReportService.getFollowUpReportData(
        selectedProject !== "all" ? selectedProject : undefined,
        dateFrom || undefined,
        dateTo || undefined,
        filters
      );

      setProjects(data.projects);
      setKpis(data.kpis);
      setBookings(data.bookings);

      // Set default project if none selected
      if (!selectedProject && data.projects.length > 0) {
        setSelectedProject("all");
      }
    } catch (err: any) {
      console.error("Error loading follow-up report:", err);
      setError(err.response?.data?.error || "Failed to load report data");
    } finally {
      setLoading(false);
    }
  };

  // Filter handlers
  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedProject(e.target.value);
  };

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateFrom(e.target.value);
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateTo(e.target.value);
  };

  const handleApplyFilters = () => {
    loadData();
  };

  const handleClearFilters = () => {
    setSelectedProject("all");
    setDateFrom("");
    setDateTo("");
    setFilterConditions([]);
    setFilterLogic("AND");
    // Reload will happen via loadData call below
    setTimeout(() => loadData(), 50);
  };

  // Dynamic filter builder handlers
  const handleAddCondition = () => {
    setFilterConditions([
      ...filterConditions,
      { field: "pendingPercent", operator: ">", value: 0 },
    ]);
  };

  const handleRemoveCondition = (index: number) => {
    setFilterConditions(filterConditions.filter((_, i) => i !== index));
  };

  const handleConditionChange = (
    index: number,
    field: keyof FilterCondition,
    value: any
  ) => {
    const updated = [...filterConditions];
    updated[index] = { ...updated[index], [field]: value };

    // Reset value when field changes
    if (field === "field") {
      // Set default operator based on field type
      const firstOperator = operatorsByField[value]?.[0]?.value || "=";
      updated[index].operator = firstOperator as any;
      
      // Set default value based on field type
      if (["paymentTerms", "unitType", "wing", "status"].includes(value)) {
        updated[index].value = "";
      } else {
        updated[index].value = 0;
      }
    }

    // Reset value when operator changes
    if (field === "operator") {
      if (value === "between") {
        updated[index].value = [0, 0];
      } else if (["paymentTerms", "unitType", "wing", "status"].includes(updated[index].field)) {
        updated[index].value = "";
      } else {
        updated[index].value = 0;
      }
    }

    setFilterConditions(updated);
  };

  const handleBetweenValueChange = (
    index: number,
    position: 0 | 1,
    value: number
  ) => {
    const updated = [...filterConditions];
    const currentValue = updated[index].value as [number, number];
    const newValue: [number, number] = [...currentValue] as [number, number];
    newValue[position] = value;
    updated[index].value = newValue;
    setFilterConditions(updated);
  };

  // Export functionality
  const handleExport = () => {
    if (bookings.length === 0) {
      alert("No data to export");
      return;
    }

    const headers = [
      "Sr. No",
      "Wing",
      "Unit",
      "Booking ID",
      "Booking Date",
      "Customer Name",
      "Contact",
      "Project",
      "Unit Type",
      "Payment Terms",
      "Total Amount",
      "Received Amount",
      "Pending Amount",
      "Pending %",
      "Payment Count",
      "Last Payment Date",
      "Last Payment Amount",
      "Days Since Booking",
      "Days Since Last Payment",
      "Status",
    ];

    const rows = bookings.map((b, index) => [
      index + 1,
      b.wing,
      b.unitRef,
      b.bookingId,
      b.bookingDate,
      b.customerName,
      b.customerContact,
      b.projectName,
      b.unitType,
      b.paymentTerms,
      b.totalAmount,
      b.receivedAmount,
      b.pendingAmount,
      b.pendingPercent,
      b.paymentCount,
      b.lastPaymentDate || "N/A",
      b.lastPaymentAmount || "N/A",
      b.daysSinceBooking,
      b.daysSinceLastPayment || "N/A",
      b.status,
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `follow-up-report-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Print functionality
  const handlePrint = () => {
    window.print();
  };

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <div className="follow-up-report-container">
      <div className="report-header">
        <h1>📊 Payment Follow-Up Report</h1>
        <p className="report-subtitle">Track pending payments and collection status</p>
      </div>

      {/* Filter Bar */}
      <div className="filter-bar" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApplyFilters(); } }}>
        <div className="filter-group">
          <label>Project:</label>
          <select value={selectedProject} onChange={handleProjectChange}>
            <option value="all">All Projects</option>
            {projects.map((proj) => (
              <option key={proj.id} value={proj.id.toString()}>
                {proj.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Booking Date From:</label>
          <input type="date" value={dateFrom} onChange={handleDateFromChange} />
        </div>

        <div className="filter-group">
          <label>Booking Date To:</label>
          <input type="date" value={dateTo} onChange={handleDateToChange} />
        </div>

        <div className="filter-actions">
          <button onClick={handleApplyFilters} className="btn-apply">
            Apply Filters
          </button>
          <button onClick={handleClearFilters} className="btn-clear">
            Clear Filters
          </button>
          <button
            onClick={() => setShowFilterBuilder(!showFilterBuilder)}
            className="btn-advanced"
          >
            {showFilterBuilder ? "Hide" : "Show"} Advanced Filters
          </button>
        </div>
      </div>

      {/* Advanced Filter Builder */}
      {showFilterBuilder && (
        <div className="filter-builder">
          <div className="filter-builder-header">
            <h3>🔍 Advanced Filter Builder</h3>
            <div className="logic-toggle">
              <label>Logic:</label>
              <select
                value={filterLogic}
                onChange={(e) => setFilterLogic(e.target.value as "AND" | "OR")}
              >
                <option value="AND">AND (All conditions must match)</option>
                <option value="OR">OR (Any condition can match)</option>
              </select>
            </div>
          </div>

          <div className="conditions-list">
            {filterConditions.map((condition, index) => (
              <div key={index} className="condition-row">
                <span className="condition-number">{index + 1}.</span>

                <select
                  value={condition.field}
                  onChange={(e) => handleConditionChange(index, "field", e.target.value)}
                >
                  {filterFields.map((field) => (
                    <option key={field.value} value={field.value}>
                      {field.label}
                    </option>
                  ))}
                </select>

                <select
                  value={condition.operator}
                  onChange={(e) =>
                    handleConditionChange(index, "operator", e.target.value)
                  }
                >
                  {operatorsByField[condition.field]?.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>

                {condition.operator === "between" ? (
                  <div className="between-inputs">
                    <input
                      type="number"
                      value={(condition.value as [number, number])[0]}
                      onChange={(e) =>
                        handleBetweenValueChange(index, 0, parseFloat(e.target.value))
                      }
                      placeholder="Min"
                    />
                    <span>to</span>
                    <input
                      type="number"
                      value={(condition.value as [number, number])[1]}
                      onChange={(e) =>
                        handleBetweenValueChange(index, 1, parseFloat(e.target.value))
                      }
                      placeholder="Max"
                    />
                  </div>
                ) : ["paymentTerms", "unitType", "wing", "status"].includes(
                    condition.field
                  ) ? (
                  <input
                    type="text"
                    value={condition.value as string}
                    onChange={(e) =>
                      handleConditionChange(index, "value", e.target.value)
                    }
                    placeholder="Enter value"
                  />
                ) : (
                  <input
                    type="number"
                    value={condition.value as number}
                    onChange={(e) =>
                      handleConditionChange(index, "value", parseFloat(e.target.value))
                    }
                    placeholder="Enter value"
                  />
                )}

                <button
                  onClick={() => handleRemoveCondition(index)}
                  className="btn-remove"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          <button onClick={handleAddCondition} className="btn-add-condition">
            + Add Condition
          </button>
        </div>
      )}

      {/* Export/Print Actions */}
      <div className="report-actions">
        <button onClick={handleExport} className="btn-export">
          📥 Export CSV
        </button>
        <button onClick={handlePrint} className="btn-print">
          🖨️ Print Report
        </button>
      </div>

      {/* Loading/Error */}
      {loading && <div className="loading">Loading report data...</div>}
      {error && <div className="error">{error}</div>}

      {!loading && !error && (
        <>
          {/* Bookings Table */}
          <div className="table-container">
            <table className="follow-up-table">
              <thead>
                <tr>
                  <th>Sr. No</th>
                  <th>Wing</th>
                  <th>Unit</th>
                  <th>Booking ID</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Contact</th>
                  <th>Project</th>
                  <th>Type</th>
                  <th>Payment Terms</th>
                  <th>Total Amount</th>
                  <th>Received</th>
                  <th>Pending</th>
                  <th>Pending %</th>
                  <th>Payments</th>
                  <th>Last Payment</th>
                  <th>Last Amt</th>
                  <th>Days Since Booking</th>
                  <th>Days Since Last Payment</th>
                </tr>
              </thead>
              <tbody>
                {bookings.length === 0 ? (
                  <tr>
                    <td colSpan={19} className="no-data">
                      No bookings found with current filters
                    </td>
                  </tr>
                ) : (
                  bookings.map((booking) => (
                    <tr
                      key={booking.bookingId}
                      className={
                        booking.pendingPercent > 70 ||
                        (booking.daysSinceLastPayment !== null &&
                          booking.daysSinceLastPayment > 90)
                          ? "high-risk-row"
                          : ""
                      }
                    >
                      <td>{bookings.indexOf(booking) + 1}</td>
                      <td>{booking.wing}</td>
                      <td>{booking.unitRef}</td>
                      <td>{booking.bookingId}</td>
                      <td>{booking.bookingDate}</td>
                      <td>{booking.customerName}</td>
                      <td>{booking.customerContact}</td>
                      <td>{booking.projectName}</td>
                      <td>{booking.unitType}</td>
                      <td>{booking.paymentTerms}</td>
                      <td>{formatCurrency(booking.totalAmount)}</td>
                      <td>{formatCurrency(booking.receivedAmount)}</td>
                      <td>{formatCurrency(booking.pendingAmount)}</td>
                      <td className="pending-percent">{booking.pendingPercent.toFixed(2)}%</td>
                      <td>{booking.paymentCount}</td>
                      <td>{booking.lastPaymentDate || "N/A"}</td>
                      <td>
                        {booking.lastPaymentAmount
                          ? formatCurrency(booking.lastPaymentAmount)
                          : "N/A"}
                      </td>
                      <td>{booking.daysSinceBooking}</td>
                      <td>{booking.daysSinceLastPayment ?? "N/A"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default FollowUpReport;
